/**
 * M4S-5777 X147: how a list row is named and where its AI workspace is, outside the classic code.
 *
 * Copied from `src/@crosstab/utils/getCrosstabRoute.ts` (read-only, deleted after the classic tool's
 * quiet period). The classic `getCrosstabRoute` had no caller left and is not copied: where a saved
 * crosstab opens is `savedCrosstabHref` / `crosstabOpenPath` (`legacy-retirement.ts`).
 *
 * AI crosstabs share `crosstab_layout` with classic ones and are told apart by `ai_layout` alone
 * (M4S-5579, surfaced on lists as `hasAiLayout`). The "DANI: " name prefix survives for display only.
 */

export const DANI_NAME_PREFIX = 'DANI: ';

const hasDaniNamePrefix = (name: string | undefined | null): boolean =>
    !!name && name.startsWith(DANI_NAME_PREFIX);

/**
 * An AI crosstab, by `crosstab_layout.ai_layout` alone. `hasAiLayout` is required so a caller that
 * has not threaded it through fails to compile; an absent value reads as classic.
 */
export const isDaniCrosstab = (item: { hasAiLayout: boolean | null | undefined }): boolean =>
    item.hasAiLayout === true;

/** Strips the legacy "DANI: " prefix from a crosstab name for display. */
export const stripDaniPrefix = (name: string): string =>
    hasDaniNamePrefix(name) ? name.slice(DANI_NAME_PREFIX.length).trim() : name;

/**
 * The `[projectId]` segment for the AI workspace URL: the Salesforce account id when there is one,
 * else the numeric project id (the OrgId rule). One function for both list views.
 */
export const aiWorkspaceRouteId = (item: {
    accountId?: string | null;
    projectId?: string | number | null;
}): string => String(item.accountId ?? item.projectId ?? '');

/**
 * The `surveys=` scope for the AI workspace URL, comma-joined, or null to leave it off. A crosstab
 * that names its own survey is scoped to it; only one with none expands to its project's list.
 */
export const aiWorkspaceSurveyScope = (
    ownSurveyId: string | number | null | undefined,
    projectSurveyIds: readonly string[] | null | undefined,
): string | null => {
    const own = ownSurveyId == null ? '' : String(ownSurveyId).trim();
    if (own) return own;

    const fromProject = (projectSurveyIds ?? []).map((s) => String(s).trim()).filter(Boolean);
    return fromProject.length ? fromProject.join(',') : null;
};
